import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";

/**
 * Accent color palettes. Each is the full 50→950 scale as "R G B" triplets,
 * matching Tailwind's named palettes so the UI keeps its shading depth.
 */
export const PALETTES = {
  teal: {
    label: "Teal",
    swatch: "#0d9488",
    shades: {
      50: "240 253 250", 100: "204 251 241", 200: "153 246 228", 300: "94 234 212",
      400: "45 212 191", 500: "20 184 166", 600: "13 148 136", 700: "15 118 110",
      800: "17 94 89", 900: "19 78 74", 950: "4 47 46",
    },
  },
  emerald: {
    label: "Green",
    swatch: "#059669",
    shades: {
      50: "236 253 245", 100: "209 250 229", 200: "167 243 208", 300: "110 231 183",
      400: "52 211 153", 500: "16 185 129", 600: "5 150 105", 700: "4 120 87",
      800: "6 95 70", 900: "6 78 59", 950: "2 44 34",
    },
  },
  blue: {
    label: "Blue",
    swatch: "#1e40af",
    shades: {
      50: "239 246 255", 100: "219 234 254", 200: "191 219 254", 300: "147 197 253",
      400: "96 165 250", 500: "59 130 246", 600: "37 99 235", 700: "30 64 175",
      800: "30 58 138", 900: "23 37 84", 950: "15 23 55",
    },
  },
  violet: {
    label: "Violet",
    swatch: "#7c3aed",
    shades: {
      50: "245 243 255", 100: "237 233 254", 200: "221 214 254", 300: "196 181 253",
      400: "167 139 250", 500: "139 92 246", 600: "124 58 237", 700: "109 40 217",
      800: "91 33 182", 900: "76 29 149", 950: "46 16 101",
    },
  },
  rose: {
    label: "Rose",
    swatch: "#e11d48",
    shades: {
      50: "255 241 242", 100: "255 228 230", 200: "254 205 211", 300: "253 164 175",
      400: "251 113 133", 500: "244 63 94", 600: "225 29 72", 700: "190 18 60",
      800: "159 18 57", 900: "136 19 55", 950: "76 5 25",
    },
  },
  amber: {
    label: "Amber",
    swatch: "#d97706",
    shades: {
      50: "255 251 235", 100: "254 243 199", 200: "253 230 138", 300: "252 211 77",
      400: "251 191 36", 500: "245 158 11", 600: "217 119 6", 700: "180 83 9",
      800: "146 64 14", 900: "120 53 15", 950: "69 26 3",
    },
  },
};

// Theme preferences are meant to outlive the browser, so they live in
// localStorage.
const STORAGE_KEY = "cortexqa-theme";

// Who is signed in is NOT a preference. The session cookie is what signs you
// in (AUTH-01; App.js asks GET users/me). The name and userkey are kept here in
// sessionStorage, for display only, so a refresh shows the app at once while
// the API is asked. It used to be localStorage, and once you had signed in on
// a machine the login page never showed again.
const SESSION_KEY = "cortexqa-session";

function applyPalette(colorKey) {
  const palette = PALETTES[colorKey] || PALETTES.teal;
  const root = document.documentElement;
  Object.entries(palette.shades).forEach(([shade, rgb]) => {
    root.style.setProperty(`--brand-${shade}`, rgb);
  });
}

// UI scale is the root font size in px. Every size in the UI is in rem, so
// this scales text, spacing and controls together.
//
// Each screen resolution has its own level, read from the monitor's CSS height
// (window.screen, so resizing the window never changes it; an ultrawide is
// judged by its height, not its width). 2K is the middle level at 22px; the
// rest keep about the same share of the screen, so the app looks alike on
// every monitor and every OS display scaling: 4K at 150% is a 2560×1440 CSS
// screen and gets 22px, about as many device pixels as 4K at 100% gets with
// 33px. (Each level was one − step lower than the first table's, 2026-11-05.)
export const RESOLUTION_LEVELS = [
  { maxHeight: 800, px: 13, label: "1366 × 768 laptop" },
  { maxHeight: 900, px: 14, label: "1600 × 900" },
  { maxHeight: 1080, px: 16, label: "Full HD" },
  { maxHeight: 1200, px: 18, label: "1920 × 1200" },
  { maxHeight: 1440, px: 22, label: "2K" },
  { maxHeight: 1600, px: 24, label: "2560 × 1600" },
  { maxHeight: 1800, px: 27, label: "3200 × 1800" },
  { maxHeight: 2160, px: 33, label: "4K" },
  { maxHeight: Infinity, px: 40, label: "5K and up" },
];

// The user's choice is a step up or down from the screen's level (− / +),
// never a px size, so it carries over to another monitor in proportion.
export const SCALE_STEP_MIN = -3;
export const SCALE_STEP_MAX = 3;

export function screenLevel(height = typeof window !== "undefined" ? window.screen?.height : 0) {
  if (!height) return RESOLUTION_LEVELS[4];
  return RESOLUTION_LEVELS.find((l) => height <= l.maxHeight);
}

// One step is about a twelfth of the level: 2px at 2K, 3px at 4K.
export function stepPx(base) {
  return Math.max(1, Math.round(base / 12));
}

export function clampStep(value) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return 0;
  return Math.min(SCALE_STEP_MAX, Math.max(SCALE_STEP_MIN, n));
}

export function scalePx(step, base = screenLevel().px) {
  return base + clampStep(step) * stepPx(base);
}

// Layout breakpoints follow the width the layout really has, in 16px units:
// a 3840px window at 36px is laid out like a 1707px one. Tailwind's sm: … 4xl:
// variants read these classes on <html> (tailwind.config.js), not the window.
export const BREAKPOINTS = { sm: 640, md: 768, lg: 1024, xl: 1280, "2xl": 1536, "3xl": 1920, "4xl": 2560 };

export function layoutWidth(px) {
  return typeof window === "undefined" ? 1280 : (window.innerWidth * 16) / px;
}

// The login page is one step smaller from 2K up (2K 20px, 4K 30px): there it
// read large next to the rest of the app.
const LOGIN_SMALLER_FROM = 1440;

function loginPx(step, onLogin) {
  const level = screenLevel();
  const px = scalePx(step, level.px);
  return onLogin && level.maxHeight >= LOGIN_SMALLER_FROM ? px - stepPx(level.px) : px;
}

function applyScale(step, onLogin = false) {
  const px = loginPx(step, onLogin);
  const root = document.documentElement;
  root.style.fontSize = `${px}px`;
  const width = layoutWidth(px);
  Object.entries(BREAKPOINTS).forEach(([name, min]) => {
    root.classList.toggle(`bp-${name}`, width >= min);
  });
  return width;
}

function applyMode(mode) {
  document.documentElement.classList.toggle("dark", mode === "dark");
}

function readStored() {
  let theme = { mode: "light", color: "blue", scaleStep: 0 };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Only the theme is taken from localStorage. Any identity left there by
      // an older build is ignored rather than restored, which is what logs a
      // returning user straight back in; the next write drops those fields.
      theme = {
        mode: parsed?.mode === "dark" ? "dark" : "light",
        color: parsed?.color || "blue",
        scaleStep: clampStep(parsed?.scaleStep ?? 0),
      };
    }
  } catch {
    /* ignore */
  }

  let session = {};
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) session = JSON.parse(raw) || {};
  } catch {
    /* ignore */
  }

  return {
    ...theme,
    username: session.username,
    email: session.email,
    userkey: session.userkey,
    userid: session.userid,
  };
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [{ mode, color, scaleStep, username, email, userkey, userid }, setState] = useState(readStored);

  useEffect(() => {
    applyMode(mode);
    applyPalette(color);
    try {
      // Theme only — writing identity here is what kept users signed in across
      // browser restarts. This also strips any identity an older build left.
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, color, scaleStep }));
    } catch {
      /* ignore */
    }
    try {
      if (userkey) {
        sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ username, email, userkey, userid })
        );
      } else {
        // Signed out, so leave nothing behind for the next visitor.
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch {
      /* ignore */
    }
  }, [mode, color, scaleStep, username, email, userkey, userid]);

  // The root size and the breakpoint classes, before paint so nothing jumps.
  // A resize moves the breakpoints; moving to another monitor (or changing
  // the OS display scaling) also fires one and may change the level.
  const [screen, setScreen] = useState(() => ({ level: screenLevel(), width: 0 }));
  const [onLogin, setOnLogin] = useState(false);
  useLayoutEffect(() => {
    const update = () => {
      const width = applyScale(scaleStep, onLogin);
      setScreen({ level: screenLevel(), width });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [scaleStep, onLogin]);

  const value = {
    mode,
    color,
    scaleStep,
    scalePx: scalePx(scaleStep, screen.level.px),
    screenLevel: screen.level,
    layoutWidth: screen.width,
    username,
    email,
    userkey,
    userid,
    palettes: PALETTES,
    setMode: (m) => setState((s) => ({ ...s, mode: m })),
    toggleMode: () => setState((s) => ({ ...s, mode: s.mode === "dark" ? "light" : "dark" })),
    setColor: (c) => setState((s) => ({ ...s, color: c })),
    setOnLogin,
    setScaleStep: (v) => setState((s) => ({ ...s, scaleStep: clampStep(v) })),
    setUsername: (u) => setState((s) => ({ ...s, username: u })),
    setEmail: (e) => setState((s) => ({ ...s, email: e })),
    setUserkey: (k) => setState((s) => ({ ...s, userkey: k })),
    setUserid: (u) => setState((s) => ({ ...s, userid: u })),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// The layout's width in 16px units (see BREAKPOINTS); outside a provider (a
// component test) the window's width.
export function useLayoutWidth() {
  const ctx = useContext(ThemeContext);
  if (ctx?.layoutWidth) return ctx.layoutWidth;
  return typeof window === "undefined" ? 1280 : window.innerWidth;
}

// The root font size in px (16 outside a provider), for sizes that have to be
// worked out in px, such as the script editor's code and its line markers.
export function useRemPx() {
  return useContext(ThemeContext)?.scalePx || 16;
}

// Login calls this while it is shown, for its smaller size; outside a provider
// (a component test) it does nothing.
export function useLoginScale() {
  const setOnLogin = useContext(ThemeContext)?.setOnLogin;
  useLayoutEffect(() => {
    if (!setOnLogin) return undefined;
    setOnLogin(true);
    return () => setOnLogin(false);
  }, [setOnLogin]);
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
